import { useState } from 'react';
import { customersBillingApi } from '@/api/customersBilling.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import type { CustomerResponse } from '@/types/customer';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (customer: CustomerResponse) => void;
  initialPhone?: string;
}

export function PosQuickAddCustomerModal({ open, onClose, onCreated, initialPhone = '' }: Props) {
  const [phone, setPhone] = useState(initialPhone);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = phone.replace(/\s+/g, '').trim();
    if (!trimmed) {
      notify.error('Phone number is required');
      return;
    }
    setSaving(true);
    try {
      const created = await customersBillingApi.quickCreate({
        phone: trimmed,
        name: name.trim() || undefined,
      });
      notify.success('Customer added');
      onCreated(created);
      setPhone('');
      setName('');
      onClose();
    } catch (err) {
      showApiError(err, 'Could not create customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Quick add customer"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save &amp; continue
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Phone number *"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
        />
        <Input
          label="Customer name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
        />
      </div>
    </Modal>
  );
}
